/**
 * Freehand-stroke geometry — PURE logic (no React, no DOM, no Yjs).
 *
 * A drawing is captured as a list of board-space points during one pointer
 * gesture, then turned into a `drawing` element (schema.ts `DrawingElementPayload`):
 * a bounding box (`x`/`y`/`w`/`h`, the element's own per-property keys) plus the
 * vertices in ELEMENT-LOCAL board units (relative to the box top-left). The box
 * is padded by half the stroke thickness so the round line caps are never
 * clipped by the element wrapper.
 *
 * Ownership ("belongs to wherever it started", docs/02 F-30-style): the anchor
 * is decided from the STROKE'S START POINT — the cell under it, or the bare
 * board — NOT from the bounding box (which may sit in a neighbour after drawing
 * up/left). A cell anchor's `dx`/`dy` therefore places the box top-left relative
 * to that cell's top-left and MAY be negative (the stroke extends above/left of
 * its start cell); the drawing does not confine to or grow the cell, so this is
 * exactly what lets it cross cell lines while staying attached.
 */
import type { Anchor, Point } from "@kola/shared";

import { cellAtPosition } from "../../core/table.js";
import type { GridLayoutSnapshot } from "../board/GridTable.js";

/**
 * Minimum board-unit gap between successive captured points — a pointer fires
 * far more often than the stroke needs, so points closer than this are dropped
 * to keep the stored `d` array small without visibly coarsening the line.
 */
export const MIN_POINT_GAP = 1.5;

/** Half-width padding floor so even a 1px line has a grabbable bounding box. */
const MIN_HALF_PAD = 1;

/** Append `next` to `points` only if it is at least `MIN_POINT_GAP` from the last. */
export function appendPoint(points: Point[], next: Point): void {
  const last = points[points.length - 1];
  if (last !== undefined) {
    const dx = next.x - last.x;
    const dy = next.y - last.y;
    if (dx * dx + dy * dy < MIN_POINT_GAP * MIN_POINT_GAP) return;
  }
  points.push({ x: next.x, y: next.y });
}

/** Axis-aligned bounding box in board units. */
export interface StrokeBounds {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * Bounding box of a stroke, padded by half the thickness on every side so the
 * round caps/joins fit inside the element. A single point (a dot) still yields
 * a `thickness`-sized box. Returns null for an empty stroke.
 */
export function strokeBounds(points: readonly Point[], thickness: number): StrokeBounds | null {
  if (points.length === 0) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of points) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }
  const pad = Math.max(MIN_HALF_PAD, thickness / 2);
  return {
    x: minX - pad,
    y: minY - pad,
    w: maxX - minX + 2 * pad,
    h: maxY - minY + 2 * pad,
  };
}

/**
 * Stroke vertices as a flat element-local array `[x0,y0,x1,y1,…]` (each point
 * minus the box top-left), rounded to whole board units — the value stored in
 * `DrawingElementPayload.d`.
 */
export function toLocalPath(points: readonly Point[], origin: { x: number; y: number }): number[] {
  const d: number[] = [];
  for (const p of points) {
    d.push(Math.round(p.x - origin.x), Math.round(p.y - origin.y));
  }
  return d;
}

/**
 * The anchor a new drawing should carry: the grid cell under its STROKE START
 * (so it FOLLOWS that cell), with the bounding-box top-left expressed as an
 * offset from the cell's top-left — UNCLAMPED, so a box that starts above/left
 * of the cell keeps a negative `dx`/`dy`. Falls back to a board anchor at the
 * box top-left when the start point is over empty canvas (or the grid isn't
 * mounted). `start` and `boxTopLeft` are absolute board-unit points.
 */
export function drawStartAnchor(
  start: Point,
  boxTopLeft: { x: number; y: number },
  layout: GridLayoutSnapshot | null,
): Anchor {
  if (layout !== null) {
    const hit = cellAtPosition(
      layout.colEdges,
      layout.rowEdges,
      start.x - layout.originX,
      start.y - layout.originY,
    );
    if (hit !== null) {
      const rowId = layout.rowIds[hit.row];
      const colId = layout.colIds[hit.col];
      if (rowId !== undefined && colId !== undefined) {
        return {
          kind: "cell",
          rowId,
          colId,
          dx: Math.round(boxTopLeft.x - (layout.originX + hit.rect.left)),
          dy: Math.round(boxTopLeft.y - (layout.originY + hit.rect.top)),
        };
      }
    }
  }
  return { kind: "board", x: Math.round(boxTopLeft.x), y: Math.round(boxTopLeft.y) };
}
